import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { CookieService } from 'ngx-cookie-service';
import { BehaviorSubject, EMPTY, forkJoin, Observable, of, throwError, timer } from 'rxjs';
import { expand, filter, map, shareReplay, switchMap, take, tap } from 'rxjs/operators';

import { FlashMessagesService } from '../../shared/modules/flash-messages';
import { ApnsListResponse } from '../models/apns-list/apns-list-response';
import { CampaignsEnableDisable } from '../models/campaigns-enable-disable/campaigns-enable-disable';
import { CampaignsListMultiResponse } from '../models/campaigns-listmulti/campaigns-listmulti-response';
import { CamapignsRevertResponse } from '../models/campaigns-revert/campaigns-revert-response';
import { CampaignsUploadResponse } from '../models/campaigns-upload/campaigns-upload-response';
import { DeviceTypesList } from '../models/device-type-list/device-type-list';
import { DevicesListResponse } from '../models/devices-list/devices-list-response';
import { DevicesvalidateResponse } from '../models/devices-validate/devices-validate-response';
import { EntitiesListResponse } from '../models/eentities-listAll/eentities-listAll-response';
import { EntitiesListFailedResponse } from '../models/eentities-listfailed/eentities-listfailed-response';
import { ExecutionsListResponse } from '../models/executions-list/executions-list-response';
import { ExecutionsRerunResponse } from '../models/executions-rerun/executions-revert-response';
import { ExecutionsRrerunResponse } from '../models/executions-rrerun/executions-r-rerun-response';
import { MnoList } from '../models/mno-list/mno-list';
import { OrgSearchResponse } from '../models/org-search/org-search-response';
import { RatezonesListResponse } from '../models/ratezones-list/ratezones-list-response';
import { SearchOmniResponse } from '../models/search-omni/search-omni-response';
import { SESSION_INFO_COMMAND, SessionInfoResponse } from '../models/session/info/session-info';
import { TranslateResponse } from '../models/translate/translate-response';
import { InstanceService } from './instance.service';

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  public campaignCreated = false;

  public get toesApi(): string {
    return this.instanceService.toesApi;
  }

  //TODO: sessionId Should never be written
  globalSessionId: string = this.cookieService.check('app-center-sessionId') && this.cookieService.get('app-center-sessionId') !== '' ? this.cookieService.get('app-center-sessionId') : '';

  get execEndpoint(): string {
    return 'https://' + this.instanceService.appCenterEndpoint.replace('http://', '').replace('https://', '');
  }

  private refreshTrigger = new BehaviorSubject<void>(undefined);
  public sessionApi: Observable<SessionInfoResponse> = this.refreshTrigger.pipe(
    // request to session api, using share replay to chache the result untill the refreshSessionInfo is exetuted
    switchMap(() => {
      return this.get<SessionInfoResponse>(SESSION_INFO_COMMAND);
    }),
    shareReplay(1)
  );

  // Force refresh of sessionApi
  public refreshSessionInfo() {
    this.refreshTrigger.next();
  }

  constructor(
    private http: HttpClient,
    private cookieService: CookieService,
    private router: Router,
    private instanceService: InstanceService,
    private flashMessageService: FlashMessagesService,
    public translate: TranslateService
  ) {}

  getExecutionEntitiesListApi(command: string, params: any): Observable<EntitiesListResponse> {
    const endpoint = `${this.toesApi}/${command}`;
    params['sessionID'] = this.globalSessionId;
    params['sourcePrefix'] = this.instanceService.prefixKey;
    return this.http.post<EntitiesListResponse>(endpoint, params, {}).pipe(tap((response) => this.handleToesResponse(response)));
  }

  getUUIDTranslatedToObjectid(uuid: string): Observable<TranslateResponse> {
    const endpoint = this.toesApi + '/monitoring/translate';

    return this.http.post<TranslateResponse>(endpoint, { key: uuid }, {}).pipe(tap((response) => this.handleToesResponse(response)));
  }

  getExecutionsListApi(command: string, params: any): Observable<ExecutionsListResponse> {
    const endpoint = `${this.toesApi}/${command}`;
    params['sessionID'] = this.globalSessionId;
    params['sourcePrefix'] = this.instanceService.prefixKey;

    return this.http.post<ExecutionsListResponse>(endpoint, params, {}).pipe(
      tap((response) => {
        if (response?.status === 'ERROR') {
          this.flashMessageService.show(response.errorMessage, {
            type: 'danger',
          });
        }
        this.handleToesResponse(response);
      })
    );
  }

  getCampaignsListMultiApi(command: string, params: any): Observable<CampaignsListMultiResponse> {
    const endpoint = `${this.toesApi}/${command}`;
    params['sessionID'] = this.globalSessionId;
    params['sourcePrefix'] = this.instanceService.prefixKey;

    return this.http.post<CampaignsListMultiResponse>(endpoint, params, {}).pipe(tap((response) => this.handleToesResponse(response)));
  }

  getEntitiesListFailedApi(command: string, params: any): Observable<EntitiesListFailedResponse> {
    const endpoint = `${this.toesApi}/${command}`;
    params['sessionID'] = this.globalSessionId;
    params['sourcePrefix'] = this.instanceService.prefixKey;

    return this.http.post<EntitiesListFailedResponse>(endpoint, params, {}).pipe(tap((response) => this.handleToesResponse(response)));
  }

  getDeviceListApi(command: string, params: any): Observable<DevicesListResponse> {
    const endpoint = `${this.toesApi}/${command}`;
    params['sessionID'] = this.globalSessionId;
    params['sourcePrefix'] = this.instanceService.prefixKey;

    return this.http.post<DevicesListResponse>(endpoint, params, {}).pipe(tap((response) => this.handleToesResponse(response)));
  }

  getTranslateBackApi(command: string, params: any): Observable<any> {
    const endpoint = `${this.toesApi}/${command}`;

    return this.http.post<any>(endpoint, params, {}).pipe(tap((response) => this.handleToesResponse(response)));
  }

  getDevicesValidateApi(command: string, params: any): Observable<DevicesvalidateResponse> {
    const endpoint = `${this.toesApi}/${command}`;
    params['sessionID'] = this.globalSessionId;
    params['sourcePrefix'] = this.instanceService.prefixKey;

    params = this.buildFormData(params);

    return this.http.post<DevicesvalidateResponse>(endpoint, params, {}).pipe(tap((response) => this.handleToesResponse(response)));
  }

  getApnsListApi(command: string, params: any): Observable<ApnsListResponse> {
    const endpoint = `${this.toesApi}/${command}`;
    params['sessionID'] = this.globalSessionId;
    params['sourcePrefix'] = this.instanceService.prefixKey;

    return this.http.post<ApnsListResponse>(endpoint, params, {}).pipe(tap((response) => this.handleToesResponse(response)));
  }

  getRatezonesListApi(command: string, params: any): Observable<RatezonesListResponse> {
    const endpoint = `${this.toesApi}/${command}`;
    params['sessionID'] = this.globalSessionId;
    params['sourcePrefix'] = this.instanceService.prefixKey;

    return this.http.post<RatezonesListResponse>(endpoint, params, {}).pipe(tap((response) => this.handleToesResponse(response)));
  }

  getCampaignsUploadApi(command: string, params: any): Observable<CampaignsUploadResponse> {
    const endpoint = `${this.toesApi}/${command}`;
    params['sessionID'] = this.globalSessionId;
    params['sourcePrefix'] = this.instanceService.prefixKey;
    params = this.buildFormData(params);

    return this.http.post<CampaignsUploadResponse>(endpoint, params, {}).pipe(tap((response) => this.handleToesResponse(response)));
  }

  getCampaignsRevertApi(command: string, params: any): Observable<CamapignsRevertResponse> {
    const endpoint = `${this.toesApi}/${command}`;
    params['sessionID'] = this.globalSessionId;
    params['sourcePrefix'] = this.instanceService.prefixKey;

    return this.http.post<CamapignsRevertResponse>(endpoint, params, {}).pipe(tap((response) => this.handleToesResponse(response)));
  }

  getExecutionsRrerunApi(command: string, params: any): Observable<ExecutionsRrerunResponse> {
    const endpoint = `${this.toesApi}/${command}`;
    params['sessionID'] = this.globalSessionId;
    params['sourcePrefix'] = this.instanceService.prefixKey;

    return this.http.post<ExecutionsRrerunResponse>(endpoint, params, {}).pipe(tap((response) => this.handleToesResponse(response)));
  }

  getExecutionsRerunApi(command: string, params: any): Observable<ExecutionsRerunResponse> {
    const endpoint = `${this.toesApi}/${command}`;
    params['sessionID'] = this.globalSessionId;
    params['sourcePrefix'] = this.instanceService.prefixKey;

    return this.http.post<ExecutionsRerunResponse>(endpoint, params, {}).pipe(tap((response) => this.handleToesResponse(response)));
  }

  getEentitiesExport(body: any): Observable<any> {
    const endpoint = `/eentities/export`;

    const __body = {
      ...body,
      sessionID: this.globalSessionId,
      sourcePrefix: this.instanceService.prefixKey,
    };

    return this.getWith202<any>(endpoint, __body);
  }

  campaignsEnableDisable(command: string, params: any): Observable<CampaignsEnableDisable> {
    const endpoint = `${this.toesApi}/${command}`;
    params['sessionID'] = this.globalSessionId;
    params['sourcePrefix'] = this.instanceService.prefixKey;

    return this.http.post<CampaignsEnableDisable>(endpoint, params, {}).pipe(tap((response) => this.handleToesResponse(response)));
  }

  getSearchOmniApi(command: string, params: object): Observable<SearchOmniResponse> {
    const data: object = {};
    const cmdObj: object = {};

    const header = 'cmd';

    data['command'] = command;
    data['params'] = params;

    if ((this.cookieService.check('app-center-sessionId') && this.cookieService.get('app-center-sessionId') !== '') || typeof params['sessionId'] !== 'undefined') {
      this.globalSessionId = this.globalSessionId || params['sessionId'];
      cmdObj['auth'] = {
        sessionId: this.globalSessionId,
      };
    }

    cmdObj[header] = data;

    let execEndpoint = this.execEndpoint;
    // cleanup
    if (execEndpoint !== '/api') {
      execEndpoint = 'https://' + execEndpoint.replace('http://', '').replace('https://', '');
    }

    return this.http.post<SearchOmniResponse>(execEndpoint, cmdObj, {}).pipe(
      tap((response: SearchOmniResponse) => {
        if (response['success'] === false) {
          this.refreshSessionInfo();
          void this.router.navigate(['/error']);
        }
      })
    );
  }

  getMnoList() {
    const endpoint = `${this.toesApi}/mno/list`;
    const params = {
      sessionID: this.globalSessionId,
      sourcePrefix: this.instanceService.prefixKey,
    };

    return this.http.post<MnoList>(endpoint, params, {}).pipe(tap((response) => this.handleToesResponse(response)));
  }

  getDeviceTypes() {
    const endpoint = `${this.toesApi}/devicetypes/list`;
    const params = {
      sessionID: this.globalSessionId,
      sourcePrefix: this.instanceService.prefixKey,
    };

    return this.http.post<DeviceTypesList>(endpoint, params, {}).pipe(tap((response) => this.handleToesResponse(response)));
  }

  private handleToesResponse(response: { status: string; errorMessage?: string }) {
    if (response.status === 'ERROR' && response.errorMessage === 'forbidden') {
      this.refreshSessionInfo();
    }
  }

  getOrgList(page = 0) {
    const pageSize = 2000;

    return this.get<OrgSearchResponse>('org.list', {
      limit: pageSize,
      offset: pageSize * page,
      show: ['id', 'key'],
    }).pipe(
      switchMap((response) => {
        if (!response['cmd']['success'] || !response.cmd.params.result || response.cmd.params.result.length === 0) {
          return of([]);
        }
        return forkJoin([of(response['cmd']['params']['result']), this.getOrgList(page + 1)]);
      }),
      map((res) => {
        return Array.isArray(res) && res.length === 0 ? [] : [...res[0], ...res[1]];
      }),
      shareReplay(1)
    );
  }

  /**
   * Function to execute an http request to server
   * all call all made by post
   * if present an app-center-sessionId will be added to the http request
   *
   * @param command command of api call
   * @param params params object passed to api call
   * @returns response of api call
   */
  get<T, S = unknown>(command: string, params?: S): Observable<T> {
    const cmdObj: { cmd: object; auth?: object } = {
      cmd: { command, params },
    };

    if (this.globalSessionId) {
      cmdObj['auth'] = {
        sessionId: this.globalSessionId,
      };
    }

    return this.http.post<T>(this.execEndpoint, cmdObj, {}).pipe(tap((res) => this.handleResponse(res, command)));
  }

  handleResponse(response: any, command: string): any {
    if (response['success'] === false && command !== 'session.info') {
      this.refreshSessionInfo();
    }
  }

  buildFormData(params: any) {
    const formData: any = new FormData();

    for (let i = 0; i < Object.keys(params).length; i++) {
      const key = Object.keys(params)[i];
      formData.append(key, params[key]);
    }
    return formData;
  }

  /**
   * GET a resource that may return 200 (ready) or 202 (accepted, not ready yet).
   */
  private getWith202<T>(url: string, body: object, options = {}): Observable<T> {
    const baseDelayMs = 1_000; // first backoff when no Retry-After
    const fetch = (endpoint: string, body: unknown, options: object) => this.http.post<T>(endpoint, body, { ...options, observe: 'response' as const });
    let key: string | undefined;

    return fetch(this.instanceService.toesApi + url, body, options).pipe(
      expand((res: any) => {
        if (res.status === 200) {
          return EMPTY;
        } // stop expanding
        if (res.status !== 202) {
          return throwError(() => new Error(`Unexpected status ${res.status}`));
        }

        const nextUrl = '/eentities/pexport';
        const delayMs = baseDelayMs;
        if (!key && res.body?.id) {
          key = res.body.id;
        }
        return timer(delayMs).pipe(switchMap(() => fetch(this.instanceService.toesApi + nextUrl, { ...body, key }, { ...options, responseType: 'blob' as 'json' })));
      }),
      // take the first 200 we see
      filter((res) => res.status === 200),
      take(1),
      map((res) => res.body)
    );
  }
}
